import { useEffect, useState } from 'react'

import { wsService } from '@/services/websocket'
import { useChartStore } from '@/stores/chartStore'
import { cn } from '@/utils/cn'

function MarketStatus({ label, isOpen }: { label: string; isOpen: boolean }) {
  return (
    <span className="flex items-center gap-1">
      <span
        className={cn('h-1.5 w-1.5 rounded-full', isOpen ? 'bg-tv-green' : 'bg-tv-text-muted')}
      />
      <span className={cn('text-2xs', isOpen ? 'text-tv-green' : 'text-tv-text-muted')}>
        {label}
      </span>
    </span>
  )
}

function useMarketStatus() {
  const now = new Date()
  const istOffset = 5.5 * 60
  const istMs = now.getTime() + (now.getTimezoneOffset() + istOffset) * 60000
  const ist = new Date(istMs)
  const istHour = ist.getHours() + ist.getMinutes() / 60
  const istDay = ist.getDay()
  const nseOpen = istDay >= 1 && istDay <= 5 && istHour >= 9.25 && istHour < 15.5

  const estOffset = -5 * 60
  const estMs = now.getTime() + (now.getTimezoneOffset() + estOffset) * 60000
  const est = new Date(estMs)
  const estHour = est.getHours() + est.getMinutes() / 60
  const estDay = est.getDay()
  const usOpen = estDay >= 1 && estDay <= 5 && estHour >= 9.5 && estHour < 16

  return { nseOpen, usOpen }
}

function useWsConnected() {
  const [connected, setConnected] = useState(wsService.isConnected())
  useEffect(() => {
    const unsub = wsService.onStateChange(setConnected)
    return () => {
      unsub()
    }
  }, [])
  return connected
}

export function StatusBar() {
  const { panels, activePanelId } = useChartStore()
  const activePanel = panels[activePanelId]
  const { nseOpen, usOpen } = useMarketStatus()
  const wsConnected = useWsConnected()

  return (
    <footer className="flex h-6 shrink-0 items-center justify-between border-t border-tv-border bg-tv-bg-secondary px-3">
      {/* Left: active symbol */}
      <div className="flex items-center gap-2">
        {activePanel && (
          <>
            <span className="font-mono text-2xs font-medium text-tv-text-primary">
              {activePanel.symbol}
            </span>
            <span className="text-2xs text-tv-text-muted">{activePanel.timeframe}</span>
            <span className="text-2xs capitalize text-tv-text-muted">{activePanel.chartType}</span>
          </>
        )}
      </div>

      {/* Center: market status */}
      <div className="flex items-center gap-3">
        <MarketStatus label="NSE" isOpen={nseOpen} />
        <MarketStatus label="US" isOpen={usOpen} />
        <MarketStatus label="Crypto" isOpen={true} />
      </div>

      {/* Right: WS connection state + version */}
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1 text-2xs">
          <span
            className={cn('h-1.5 w-1.5 rounded-full', wsConnected ? 'bg-tv-green' : 'bg-tv-red')}
          />
          <span className={wsConnected ? 'text-tv-green' : 'text-tv-text-muted'}>
            {wsConnected ? 'Live' : 'Connecting…'}
          </span>
        </span>
        <span className="text-2xs text-tv-text-muted">v0.10.0</span>
      </div>
    </footer>
  )
}
